/**
 * Grid-based spatial index for marker ids.
 *
 * Why this file exists:
 * - A full scan of all markers on every camera change is too expensive at scale.
 * - This index reduces work by querying only buckets that intersect the current viewport.
 *
 * Algorithm details:
 * - Space is partitioned into fixed-size latitude/longitude grid buckets.
 * - Each marker record stores its current `spatialKey` for O(1) bucket updates/removals.
 * - Viewport query expands bucket bounds by one cell to avoid edge misses caused by rounding.
 * - Dateline-wrapping viewports are handled by splitting lookup ranges.
 *
 * Result:
 * - Visibility updates consider far fewer candidates while keeping correctness near boundaries.
 * 
 * IMPORTANT CONTRACT:
 *
 * This spatial index mutates the provided record objects.
 *
 * Specifically:
 * - record.spatialKey is assigned and updated internally
 * - record is considered "owned jointly" between caller and index
 *
 * This design avoids maintaining a secondary lookup map,
 * but introduces a controlled mutation side effect.
 *
 * Callers must NOT assume records are immutable after insertion.
 */
import { GRID_SIZE, getSpatialKey } from './utils';

export default function createSpatialIndex({ gridSize = GRID_SIZE } = {}) {
  const bucketsByKey = new Map();

  /**
   * Single source of truth for bucket key generation.
   * MUST match getSpatialKey behavior.
   */
  function makeKey(lat, lng) {
    return `${lat},${lng}`;
  }

  function removeFromBucket(spatialKey, markerId) {
    if (!spatialKey) {
      return;
    }

    const bucket = bucketsByKey.get(spatialKey);
    if (!bucket) {
      return;
    }

    bucket.delete(markerId);

    if (bucket.size === 0) {
      bucketsByKey.delete(spatialKey);
    }
  }

  /**
   * Updates the spatial bucket assignment for a record.
   *
   * Side effect:
   * - Mutates record.spatialKey to reflect its current bucket
   */
  function updateRecord(record, newPosition = null) {
    if (!record?.markerData?.position) {
      return;
    }

    if (record.spatialKey) {
      removeFromBucket(record.spatialKey, record.markerData.id);
    }

    const position = newPosition || record.markerData.position;
    const nextKey = getSpatialKey(position, gridSize);

    if (!bucketsByKey.has(nextKey)) {
      bucketsByKey.set(nextKey, new Set());
    }

    bucketsByKey.get(nextKey).add(record.markerData.id);
    record.spatialKey = nextKey;
  }

  function getViewportMarkerIds(bounds) {
    const markerIds = new Set();

    if (!bounds) {
      return markerIds;
    }

    const latMin = Math.floor(bounds.south / gridSize) - 1;
    const latMax = Math.ceil(bounds.north / gridSize) + 1;
    const lngMin = Math.floor(bounds.west / gridSize) - 1;
    const lngMax = Math.ceil(bounds.east / gridSize) + 1;

    const maxLngIndex = 180 / gridSize;
    const minLngIndex = -180 / gridSize;

    const scan = (latStart, latEnd, lngStart, lngEnd) => {
      // Iterates over all grid buckets that intersect a rectangular viewport region.
      // Each (lat, lng) pair represents a spatial bucket key in the grid index.
      // This is used to collect candidate markers before applying visibility rules.
      for (let lat = latStart; lat <= latEnd; lat += 1) {
        for (let lng = lngStart; lng <= lngEnd; lng += 1) {
          const bucket = bucketsByKey.get(makeKey(lat, lng));

          if (bucket) {
            bucket.forEach((id) => markerIds.add(id));
          }
        }
      }
    };

    if (bounds.west <= bounds.east) {
      // Normal case: viewport does not cross antimeridian
      scan(latMin, latMax, lngMin, lngMax);
      return markerIds;
    }

    // Special case: viewport crosses dateline (±180° longitude)
    // We split the longitude range into two valid grid segments
    scan(latMin, latMax, lngMin, maxLngIndex);
    scan(latMin, latMax, minLngIndex, lngMax);

    return markerIds;
  }

  function removeRecord(record) {
    if (!record?.spatialKey) {
      return;
    }

    removeFromBucket(record.spatialKey, record.markerData.id);
    record.spatialKey = null;
  }

  function clear() {
    bucketsByKey.clear();
  }

  return {
    updateRecord,
    removeRecord,
    getViewportMarkerIds,
    clear,
  };
}
